import { useEffect, useState } from "react";
import { BACKEND_URL, makeRequestAuth } from "../../_config/api";
import axios from "axios";
import { showErrorToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";

export default function PropertyTable() {
    const [loading, setLoading] = useState(true);
    const [excelLoading, setExcelLoading] = useState(false);
    const [activeTab, setActiveTab] = useState("loan");
    const [pageNo, setPageNo] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [filters, setFilters] = useState({
        loanType: "",
        status: "",
        fromDate: "",
        toDate: "",
    });

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectReason, setRejectReason] = useState("");
    const [selectedId, setSelectedId] = useState(null);

    const [search, setSearch] = useState("");
    const [updatingId, setUpdatingId] = useState(null);
    const [excelModalOpen, setExcelModalOpen] = useState(false);
    const pageSize = 10;
    const [data, setData] = useState([]);

    // Fetch API
    const fetchBanks = async (page = 1, keyword = "") => {
        try {
            setLoading(true);
            const payload = {
                pageNo: page,
                size: pageSize,
                keyWord: keyword,
                ...(filters.fromDate ? { fromDate: filters.fromDate } : {}),
                ...(filters.toDate ? { toDate: filters.toDate } : {}),
            };

            if (activeTab === "loan") {
                if (filters.loanType) payload.loanType = filters.loanType;
                if (filters.status) payload.status = filters.status;
            } else {
                payload.sortOrder = "desc";
                payload.sortBy = "createdAt";
            }

            const endpoint = activeTab === "loan" ? "/admin/getLoanQueryList" : "/admin/getInterestedQueryList";
            const res = await makeRequestAuth("post", endpoint, payload);

            if (res?.error === false) {
                setData(res.data.list);
                setTotalPages(Math.ceil(res.data.total / pageSize));
            } else {
                setData([]);
                setTotalPages(1);
            }
        } catch (err) {
            setData([]);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };


    // Page change
    useEffect(() => {
        const timer = setTimeout(() => {
            fetchBanks(pageNo, search);
        }, 200); // 🔥 fast response

        return () => clearTimeout(timer);
    }, [search, pageNo, filters, activeTab]);



    // Search with debounce
    const handleSearch = (e) => {
        setSearch(e.target.value);
        setPageNo(1);
    };

    // Status change handle karein
    const handleStatusUpdate = async (id, status) => {
        if (status === "rejected") {
            setSelectedId(id);
            setShowRejectModal(true); // Modal open karein
        } else {
            await updateStatusAPI(id, status);
        }
    };

    const updateStatusAPI = async (id, status, reason = "") => {
        try {
            setUpdatingId(id);
            const res = await makeRequestAuth("post", "/admin/updateLoanQueryStatus", {
                id,
                status,
                reason
            });

            if (!res?.error) {
                setData(prev => prev.map(item =>
                    item._id === id ? { ...item, status, rejectReason: reason } : item
                ));
                setShowRejectModal(false);
                setRejectReason("");
            }
        }

        catch (err) {
            showErrorToast(err.message)
        }
        finally {
            setUpdatingId(null);
        }
    };

    const handleExcelDownload = async () => {
        setExcelModalOpen(false);
        const { token } = JSON.parse(sessionStorage.getItem("user")) || {}
        if (!token) return

        if (data.length === 0) {
            showErrorToast(
                "No records found. Try clearing the filters or changing your search, then download again."
            );
            return
        }
        setExcelLoading(true)
        try {
            const res = await axios.post(
                BACKEND_URL + "/admin/exportLoanQueryExcel",
                {
                    keyWord: search,
                    loanType: filters.loanType,
                    status: filters.status,
                    fromDate: filters.fromDate,
                    toDate: filters.toDate,
                },
                {
                    responseType: "blob",
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            if (res.data.type === "application/json") {
                const text = await res.data.text();
                const json = JSON.parse(text);

                if (json.error) {
                    return showErrorToast(json.message);
                }
            }

            const fileBlob =
                res instanceof Blob
                    ? res
                    : res?.data instanceof Blob
                        ? res.data
                        : new Blob([res], {
                            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                        });


            const url = window.URL.createObjectURL(fileBlob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `loan_queries_${Date.now()}_sheet.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

        } catch (err) {
            showErrorToast("Excel download failed");
        } finally {
            setExcelLoading(false)
        }
    };


    const prevPage = () => pageNo > 1 && setPageNo(pageNo - 1);
    const nextPage = () => pageNo < totalPages && setPageNo(pageNo + 1);

    return (
        <div className="p-6 min-h-screen bg-white">
            <ToastContainer />

            <div className="mb-6">
                <h2 className="text-2xl font-semibold text-[#434646] mb-4">
                    Loan Center
                </h2>
                <div className="flex gap-6 border-b border-gray-200">
                    <button
                        onClick={() => { setActiveTab("loan"); setPageNo(1); }}
                        className={`pb-3 font-semibold text-sm transition-all ${activeTab === 'loan' ? 'text-[#24ADD7] border-b-2 border-[#24ADD7]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Home Loan Queries
                    </button>
                    <button
                        onClick={() => { setActiveTab("credit"); setPageNo(1); }}
                        className={`pb-3 font-semibold text-sm transition-all ${activeTab === 'credit' ? 'text-[#24ADD7] border-b-2 border-[#24ADD7]' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                        Credit Score Leads
                    </button>
                </div>
            </div>

            {/* Search */}
            <div className="bg-white border border-gray-200 rounded-2xl p-4 mb-6 shadow-sm">
                <div className="flex flex-wrap gap-4 items-end">

                    {/* 🔍 Search */}
                    <div className="relative flex-1 min-w-[220px]">
                        <input
                            type="text"
                            placeholder="Search by name or phone..."
                            value={search}
                            onChange={handleSearch}
                            className="pl-10 pr-4 py-2 border border-gray-200 rounded-xl w-full
        text-sm text-[#434646] focus:border-[#24ADD7] outline-none"
                        />
                        <svg
                            className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>

                    {/* Loan Type */}
                    {activeTab === "loan" && (
                        <select
                            value={filters.loanType}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, loanType: e.target.value }))
                            }
                            className="px-4 py-2 rounded-xl border border-gray-200 text-sm
      text-[#434646] focus:border-[#24ADD7] outline-none"
                        >
                            <option value="">All Loan Types</option>
                            <option value="home_loan">Home Loan</option>
                            <option value="personal_loan">Personal Loan</option>
                            <option value="business_loan">Business Loan</option>
                            <option value="loan_against_property">Loan Against Property</option>
                        </select>
                    )}

                    {/* Status */}
                    {activeTab === "loan" && (
                        <select
                            value={filters.status}
                            onChange={(e) =>
                                setFilters((p) => ({ ...p, status: e.target.value }))
                            }
                            className="px-4 py-2 rounded-xl border border-gray-200 text-sm
      text-[#434646] focus:border-[#24ADD7] outline-none"
                        >
                            <option value="">All Status</option>
                            <option value="pending">Pending</option>
                            <option value="documents_collection">Documents Collection</option>
                            <option value="eligibility_check">Eligibility Check</option>
                            <option value="approved">Approved</option>
                            <option value="disbursement">Disbursement</option>
                            <option value="rejected">Rejected</option>
                            <option value="cancelled">Cancelled</option>
                        </select>
                    )}

                    {/* Clear All */}
                    {(filters.loanType || filters.status || filters.fromDate || filters.toDate || search) && (
                        <button
                            onClick={() => {
                                setSearch("");
                                setFilters({
                                    loanType: "",
                                    status: "",
                                    fromDate: "",
                                    toDate: "",
                                });
                                setPageNo(1);
                            }}
                            className="px-4 py-2 rounded-xl text-sm font-semibold
        border border-[#24ADD7] text-[#24ADD7]
        hover:bg-[#24ADD7] hover:text-white transition"
                        >
                            Clear All
                        </button>
                    )}

                    {/* Excel Download */}
                    {activeTab === "loan" && (
                        <button
                            onClick={() => setExcelModalOpen(true)}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold
  bg-[#24ADD7] text-white shadow-sm
  hover:bg-[#106D87] transition whitespace-nowrap"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 16v-8m0 8l-3-3m3 3l3-3M4 20h16"
                                />
                            </svg>
                            Export Excel
                        </button>
                    )}

                </div>
                {/* Export Button */}

            </div>




            {/* Table Container - Responsive */}
            <div className="overflow-x-auto bg-white rounded-2xl shadow-xl border border-gray-100">
                <table className="w-full min-w-[600px]">
                    <thead className="bg-gradient-to-r from-[#24ADD7] to-orange-500 sticky top-0">
                        <tr>
                            <th className="p-3 text-left font-semibold text-white text-sm">#</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">Date</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">Name</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">Phone</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">{activeTab === 'loan' ? 'Loan Type' : 'Query Details'}</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">City</th>
                            <th className="p-3 text-left font-semibold text-white text-sm">Status</th>
                        </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-100">

                        {/* 🔄 LOADING STATE */}
                        {loading && (
                            <tr>
                                <td colSpan={7} className="text-center py-12">
                                    <div className="flex flex-col items-center gap-3 text-gray-400">
                                        <svg className="animate-spin w-6 h-6" viewBox="0 0 24 24">
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                                stroke="currentColor"
                                                strokeWidth="4"
                                                fill="none"
                                                opacity="0.25"
                                            />
                                            <path
                                                fill="currentColor"
                                                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                            />
                                        </svg>
                                        Loading loan queries...
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* ❌ NO DATA */}
                        {!loading && data.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center py-12">
                                    <div className="flex flex-col items-center gap-2 text-gray-400">
                                        <svg className="w-10 h-10 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5" />
                                        </svg>
                                        <p className="font-medium">No loan queries found</p>
                                        <p className="text-xs">
                                            Try changing filters or clearing search
                                        </p>
                                    </div>
                                </td>
                            </tr>
                        )}

                        {/* ✅ DATA ROWS */}
                        {data.map((item, idx) => (
                            <tr
                                key={item._id}
                                className="transition-all duration-200 hover:bg-orange-50/50 hover:shadow-inner"
                            >
                                <td className="p-3 font-medium text-[#434646] text-sm border-r border-gray-100">
                                    {(pageNo - 1) * pageSize + idx + 1}
                                </td>

                                <td className="p-3 text-xs text-[#434646] text-opacity-70">
                                    {new Date(item.createdAt).toLocaleDateString()}
                                </td>

                                <td className="p-3 font-semibold text-[#434646] text-sm border-r border-gray-100 capitalize">
                                    {item.name || "-"}
                                </td>

                                <td className="p-3 text-[#434646] text-sm border-r border-gray-100">
                                    {item.phone}
                                </td>

                                <td className="p-3 text-[#434646] text-sm capitalize border-r border-gray-100">
                                    {activeTab === 'loan' ? item.loanType?.replace("_", " ") : item.query || "-"}
                                </td>

                                <td className="p-3 text-[#434646] text-sm border-r border-gray-100 capitalize">
                                    {item.city}
                                </td>

                                <td className="p-3 border-r border-gray-100">
                                    {activeTab === 'loan' ? (
                                        <div className="flex flex-col gap-1">
                                            {item.status === 'cancelled' ? (
                                                <span className="px-3 py-1 rounded-full text-xs font-semibold border bg-white border-red-300 text-red-600 inline-block text-center cursor-not-allowed">
                                                    Cancelled by User
                                                </span>
                                            ) : (
                                                <select
                                                    value={item.status || "pending"}
                                                    disabled={updatingId === item._id}
                                                    onChange={(e) => handleStatusUpdate(item._id, e.target.value)}
                                                    className={`px-3 py-1 rounded-full text-xs font-semibold border bg-white
                ${item.status === "approved" ? "border-green-300 text-green-600" :
                                                            item.status === "rejected" ? "border-red-300 text-red-600" :
                                                                "border-[#24ADD7]/40 text-[#24ADD7]"}`}
                                                >
                                                    <option value="pending">Pending</option>
                                                    <option value="documents_collection">Documents Collection</option>
                                                    <option value="eligibility_check">Eligibility Check</option>
                                                    <option value="approved">Approved</option>
                                                    <option value="disbursement">Disbursement</option>
                                                    <option value="rejected">Rejected</option>
                                                </select>
                                            )}

                                            {/* Modern Reason Badge */}
                                            {item.status === "rejected" && item.rejectReason && (
                                                <div className="mt-1 px-2 py-1 bg-red-50 rounded border border-red-100">
                                                    <p className="text-[10px] text-red-600 leading-tight">
                                                        <span className="font-bold">Reason:</span> {item.rejectReason}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div className="px-3 py-1 text-center rounded-full text-xs font-semibold border border-blue-300 text-blue-600 bg-blue-50">
                                            Lead Captured
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Rejection Reason Modal */}
            {showRejectModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl border border-gray-100 animate-in fade-in zoom-in duration-200">
                        <h3 className="text-xl font-bold text-[#434646] mb-2">Rejection Reason</h3>
                        <p className="text-sm text-gray-500 mb-4">Please provide a valid reason for rejecting this application.</p>

                        <textarea
                            className="w-full p-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#24ADD7] focus:border-transparent outline-none text-sm min-h-[120px] resize-none"
                            placeholder="Type reason here..."
                            value={rejectReason}
                            onChange={(e) => setRejectReason(e.target.value)}
                        />

                        <div className="flex gap-3 mt-6">
                            <button
                                onClick={() => {
                                    setShowRejectModal(false);
                                    setRejectReason("");
                                }}
                                className="flex-1 px-4 py-2 text-sm font-semibold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => updateStatusAPI(selectedId, "rejected", rejectReason)}
                                disabled={!rejectReason.trim()}
                                className="flex-1 px-4 py-2 text-sm font-semibold text-white bg-[#24ADD7] rounded-xl hover:bg-[#e85d1f] transition disabled:opacity-50"
                            >
                                Confirm Reject
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-6 p-3 bg-white rounded-2xl shadow-lg border border-gray-100">
                    <button
                        onClick={prevPage}
                        disabled={pageNo === 1}
                        className="px-4 py-2 border-2 border-[#24ADD7] rounded-xl font-semibold text-[#24ADD7] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#24ADD7] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md disabled:shadow-none text-sm"
                    >
                        ← Prev
                    </button>

                    <span className="text-[#434646] font-semibold text-base">
                        Page {pageNo} of {totalPages}
                    </span>

                    <button
                        onClick={nextPage}
                        disabled={pageNo === totalPages}
                        className="px-4 py-2 border-2 border-[#24ADD7] rounded-xl font-semibold text-[#24ADD7] disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#24ADD7] hover:text-white transition-all duration-200 shadow-sm hover:shadow-md disabled:shadow-none text-sm"
                    >
                        Next →
                    </button>
                </div>
            )}
{excelModalOpen && (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm px-4">
        <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl p-6">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-extrabold text-slate-800">Export Excel</h3>
                <button
                    onClick={() => setExcelModalOpen(false)}
                    className="text-gray-400 hover:text-red-500 transition-colors"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
            </div>
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">Start Date</label>
                    <input
                        type="date"
                        value={filters.fromDate}
                        onChange={(e) =>
                            setFilters((p) => ({ ...p, fromDate: e.target.value }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#434646] focus:border-[#24ADD7] outline-none"
                    />
                </div>
                <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1">End Date</label>
                    <input
                        type="date"
                        value={filters.toDate}
                        min={filters.fromDate}
                        onChange={(e) =>
                            setFilters((p) => ({ ...p, toDate: e.target.value }))
                        }
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-[#434646] focus:border-[#24ADD7] outline-none"
                    />
                </div>
            </div>
            <div className="flex justify-end gap-3 mt-8">
                <button
                    onClick={() => setExcelModalOpen(false)}
                    className="px-5 py-2.5 border border-gray-200 rounded-xl text-sm font-bold text-gray-600 hover:bg-gray-50 transition-all"
                >
                    Cancel
                </button>
                <button
                    onClick={handleExcelDownload}
                    className="flex items-center gap-2 px-6 py-2.5 bg-[#24ADD7] text-white rounded-xl text-sm font-black hover:bg-[#198cae] transition-all shadow-lg shadow-cyan-100"
                >
                    Export
                </button>
            </div>
        </div>
    </div>
)}

        </div>
    );
}